/**
 * One paper, and everybody who has sat it.
 *
 * The point of the list this hangs off is to get here: a paper on its own says
 * very little, and what an operator actually wants is whose work is behind it
 * and whether that work has been turned into anything.
 *
 * The Generate action exists because report generation rides on the session
 * write-back, which fires as each analysis result lands and never fires again.
 * Every sitting that predates the feature — which today is all of them — needs
 * somebody to ask.
 *
 * The Detect-moments actions exist for the same reason one layer down: learning-
 * moment detection rides on each analysis, once, and only when the student's
 * auto-detect gate is on. A moment authored after the sitting, or a sitting
 * analysed with the gate off, gets its moments only when somebody asks — for
 * one sitting from its row, or for the whole paper from the header.
 */

import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { format } from 'date-fns';
import { DataTable, Badge, BackButton, type ColumnDef } from '@shared-ui/components';
import { adminApi, type AdminSittingRow } from '../../api/adminApi';
import './AssignmentsAndTests.css';

const when = (iso?: string) => (iso ? format(new Date(iso), 'MMM d, HH:mm') : '—');

export function PaperSittings() {
  const { questionSetId } = useParams<{ questionSetId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [note, setNote] = useState<{ kind: 'ok' | 'err'; text: string } | null>(null);

  const { data, isLoading, error } = useQuery({
    queryKey: ['question-set-sittings', questionSetId],
    queryFn: () => adminApi.getQuestionSetSittings(questionSetId!),
    enabled: !!questionSetId,
  });

  const generate = useMutation({
    mutationFn: (sessionId: string) => adminApi.generateTestReportForSitting(sessionId),
    onSuccess: res => {
      setNote({
        kind: 'ok',
        text: res.existingReportId
          ? 'This sitting already has a report — the job will decline to write a second one.'
          : `Queued. ${res.results} question${res.results === 1 ? '' : 's'}${res.hasMarkingScheme ? '' : ', no marking scheme, so the report will cover conversion without marks'}. Watch Monitoring → Async Jobs.`,
      });
      // The report id lands only once the job runs, so this refetch shows the
      // row unchanged. It is here for the second visit, not the first.
      queryClient.invalidateQueries({ queryKey: ['question-set-sittings', questionSetId] });
    },
    onError: (e: unknown) => setNote({ kind: 'err', text: e instanceof Error ? e.message : 'Could not queue the report.' }),
  });

  const detectOne = useMutation({
    mutationFn: (sessionId: string) => adminApi.detectLearningMomentsForSitting(sessionId),
    onSuccess: res => setNote({
      kind: 'ok',
      text: res.alreadyRunning
        ? 'A detection run for this sitting is already on its way — nothing new was queued.'
        : `Queued: ${res.questions} question${res.questions === 1 ? '' : 's'}, ${res.combined ? 'one call per chunk of the sitting' : 'one detection call each'}. Watch Monitoring → Async Jobs.`,
    }),
    onError: (e: unknown) => setNote({ kind: 'err', text: e instanceof Error ? e.message : 'Could not queue detection.' }),
  });

  const detectAll = useMutation({
    mutationFn: () => adminApi.detectLearningMomentsForPaper(questionSetId!),
    onSuccess: res => {
      const fresh = res.queued.filter(q => !q.alreadyRunning).length;
      const running = res.queued.length - fresh;
      const combined = res.queued.filter(q => q.combined).length;
      setNote({
        kind: 'ok',
        text: [
          `Queued ${fresh} sitting${fresh === 1 ? '' : 's'}${combined > 0 ? ` (${combined} as whole-sitting calls)` : ' (one call per question)'}.`,
          running > 0 ? `${running} already running.` : '',
          res.skipped.length > 0 ? `${res.skipped.length} not completed, left alone.` : '',
          'Watch Monitoring → Async Jobs.',
        ].filter(Boolean).join(' '),
      });
    },
    onError: (e: unknown) => setNote({ kind: 'err', text: e instanceof Error ? e.message : 'Could not queue detection.' }),
  });

  const paper = data?.paper;
  const sittings = data?.sittings ?? [];
  const isTest = paper?.modality === 'test';
  const completed = sittings.filter(r => r.status === 'completed').length;

  const columns: ColumnDef<AdminSittingRow>[] = [
    {
      key: 'student',
      header: 'Student',
      render: r => (
        <div>
          <div>{r.studentName ?? <span className="aat-mono">{r.userId.slice(0, 8)}</span>}</div>
          <div className="aat-sub aat-mono">{r.sessionId.slice(0, 8)}</div>
        </div>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      render: r => (
        <Badge variant={r.status === 'completed' ? 'success' : 'default'} size="sm">{r.status}</Badge>
      ),
    },
    {
      key: 'questions',
      header: 'Questions',
      render: r => (
        <span className="aat-num">
          {r.results}
          {r.pending > 0 && <span className="aat-sub"> · {r.pending} still analysing</span>}
        </span>
      ),
    },
    { key: 'started', header: 'Started', render: r => when(r.startedAt) },
    { key: 'ended', header: 'Finished', render: r => when(r.endedAt) },
    {
      // Model spend on this attempt: page splits (split + page reader) and
      // every answer's analysis, re-evaluations included.
      key: 'cost',
      header: 'LLM cost',
      render: r => r.llmCostUsd === undefined
        ? <span className="aat-sub">—</span>
        : `$${r.llmCostUsd >= 1 ? r.llmCostUsd.toFixed(2) : r.llmCostUsd.toFixed(4)}`,
    },
    {
      key: 'moments',
      header: 'Moments',
      render: r => {
        if (r.status !== 'completed') return <span className="aat-sub">not completed</span>;
        const busy = detectOne.isPending && detectOne.variables === r.sessionId;
        return (
          <button
            className="btn-secondary"
            disabled={detectOne.isPending || detectAll.isPending}
            onClick={e => { e.stopPropagation(); setNote(null); detectOne.mutate(r.sessionId); }}
            title="Re-detect learning moments for every analysed question of this sitting — one call per chunk or one per question, as the student's sitting-detect gate says; replaces its earlier encounters"
          >
            {busy ? 'Queueing…' : 'Detect'}
          </button>
        );
      },
    },
    ...(isTest ? [{
      key: 'checks',
      header: 'Checks',
      render: (r: AdminSittingRow) => {
        if (!r.reportId) return <span className="aat-sub">—</span>;
        // Silence is the pass. A column that says "OK" on every good report
        // trains the eye to skip it, and then it is not read on the one row
        // that matters.
        if (!r.reviewFlagCount) return <span className="aat-sub">clear</span>;
        return (
          <Badge variant="warning" size="sm">
            {r.reviewFlagCount} to check
          </Badge>
        );
      },
    } as ColumnDef<AdminSittingRow>] : []),
    ...(isTest ? [{
      key: 'report',
      header: 'Report',
      render: (r: AdminSittingRow) => {
        if (r.reportId) {
          // A link, not a badge alone: a status with nowhere to go was the
          // whole complaint — the reader wants to READ what was generated.
          return (
            <button
              className="btn-link"
              onClick={e => { e.stopPropagation(); navigate(`/assignments-tests/report/${r.reportId}?studentId=${r.userId}`); }}
            >
              <Badge variant={r.reportStatus === 'released' ? 'success' : 'default'} size="sm">{r.reportStatus}</Badge>
              <span className="trv-open"> open</span>
            </button>
          );
        }
        // Every reason the request would be refused, stated instead of offered.
        const blocked = r.status !== 'completed'
          ? 'not completed'
          : r.pending > 0
            ? 'still analysing'
            : null;
        if (blocked) return <span className="aat-sub">{blocked}</span>;
        return (
          <div className="trv-gen">
            <button
              className="btn-secondary"
              disabled={generate.isPending}
              onClick={() => { setNote(null); generate.mutate(r.sessionId); }}
            >
              {generate.isPending && generate.variables === r.sessionId
                ? 'Queueing…'
                : r.lastJobStatus === 'failed' ? 'Try again' : 'Generate'}
            </button>
            {/* A previous attempt that produced nothing. Shown here rather than
                left in Async Jobs, because this is where somebody pressed the
                button and watched nothing happen. */}
            {r.lastJobStatus && r.lastJobStatus !== 'succeeded' && (
              <div className={r.lastJobStatus === 'failed' ? 'trv-fail' : 'aat-sub'}>
                {r.lastJobStatus === 'failed' ? 'Last attempt failed' : `Last attempt: ${r.lastJobStatus}`}
                {r.lastJobError && <div className="trv-fail-why">{r.lastJobError}</div>}
              </div>
            )}
          </div>
        );
      },
    }] : []),
  ];

  return (
    <div className="aat-page">
      <BackButton onClick={() => navigate('/assignments-tests')} label="Assignments & Tests" />

      <div className="aat-head">
        <h1 className="aat-h1">{paper?.title ?? 'Paper'}</h1>
        {paper && (
          <span className="aat-count">
            {paper.modality === 'practice' ? 'Assignment' : 'Test'}
            {paper.modality === 'test' && ` · ${paper.hasMarkingScheme ? 'has a marking scheme' : 'no marking scheme'}`}
            {` · ${sittings.length} sitting${sittings.length === 1 ? '' : 's'}`}
          </span>
        )}
        {completed > 0 && (
          <button
            className="btn-secondary aat-head-action"
            disabled={detectAll.isPending || detectOne.isPending}
            onClick={() => { setNote(null); detectAll.mutate(); }}
            title="Re-detect learning moments for every completed sitting of this paper — one job per sitting; per sitting or per question as each student's gate says"
          >
            {detectAll.isPending ? 'Queueing…' : `Detect moments · ${completed} sitting${completed === 1 ? '' : 's'}`}
          </button>
        )}
      </div>

      {/* An assignment cannot produce a test report: practice does not score,
          and the report is about converting knowledge into marks under test
          conditions. Said once here rather than as a disabled button per row. */}
      {paper && !isTest && (
        <div className="aat-note">
          This is an assignment. Test reports are not generated for practice sittings — practice does not score.
        </div>
      )}

      {note && <div className={note.kind === 'ok' ? 'aat-note' : 'aat-error'}>{note.text}</div>}
      {error && <div className="aat-error">{error instanceof Error ? error.message : 'Could not load the sittings.'}</div>}

      <DataTable<AdminSittingRow>
        columns={columns}
        data={sittings}
        rowKey={r => r.sessionId}
        loading={isLoading}
        compact
        emptyMessage="Nobody has sat this paper yet."
        rowTestId="sitting-row"
      />
    </div>
  );
}
